import { useState } from "react"
import {
    Typography,
    Button,
    Card,
    Switch,
    Select,
    Checkbox,
    message,
    List,
    Tag,
} from "antd"
import {
    SyncOutlined,
} from "@ant-design/icons"

const { Title, Text } = Typography
const { Option } = Select

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const ExecutionControl = () => {
    const [requireAdminApproval, setRequireAdminApproval] = useState(false)
    const [approverRoles, setApproverRoles] = useState([])
    const [notifyApprovers, setNotifyApprovers] = useState(false)
    const [warnBeforeExecuting, setWarnBeforeExecuting] = useState(false)

    const handleSimulateExecution = () => {
        showMessage("info", "Simulating next execution...")
        // In a real app, this would trigger a backend simulation
    }

    const integrationData = [
        { id: "asana", name: "Asana", autoExecute: true, notes: null },
        { id: "jira", name: "Jira", autoExecute: false, notes: "approval required by org policy" },
        { id: "salesforce", name: "Salesforce", autoExecute: true, notes: null },
    ]

    const [integrations, setIntegrations] = useState(integrationData)

    const handleIntegrationToggle = (id, checked) => {
        setIntegrations(
            integrations.map((integration) =>
                integration.id === id ? { ...integration, autoExecute: checked } : integration,
            ),
        )
    }

    const executionPreviewData = [
        { id: 1, action: "Create task in Asana: Launch risk response", status: "Pending" },
        { id: 2, action: "Send alert to Slack: CAC exceeded", status: "Pending" },
        { id: 3, action: "Update record in Salesforce: Lead status change", status: "Pending" },
    ]

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Execution Control</Title>
                <Text type="secondary">Manage auto-execution rules and admin approval settings.</Text>
            </div>

            <div className="mb-6">
                <div className="flex items-center justify-between mb-4">
                    <Text strong>Require Admin Approval Before Auto-Execution</Text>
                    <Switch checked={requireAdminApproval} onChange={setRequireAdminApproval} />
                </div>

                {requireAdminApproval ? (
                    <div className="ml-8 grid gap-4">
                        <div>
                            <Text>Select approver roles/users:</Text>
                            <Select
                                mode="multiple"
                                placeholder="Select roles or users"
                                value={approverRoles}
                                onChange={setApproverRoles}
                                className="w-full mt-2"
                            >
                                <Option value="admin">Admin</Option>
                                <Option value="manager">Manager</Option>
                                <Option value="john_doe">John Doe</Option>
                            </Select>
                        </div>
                        <Checkbox checked={notifyApprovers} onChange={(e) => setNotifyApprovers(e.target.checked)}>
                            Notify approvers via Slack or Email
                        </Checkbox>
                    </div>
                ) : (
                    <div className="ml-8 flex items-center gap-4">
                        <Checkbox checked={warnBeforeExecuting} onChange={(e) => setWarnBeforeExecuting(e.target.checked)}>
                            Warn user before executing
                        </Checkbox>
                        <Button onClick={handleSimulateExecution}>Simulate Next Execution</Button>
                    </div>
                )}
            </div>

            <Card title="Execution Preview" className="mb-6">
                <List
                    dataSource={executionPreviewData}
                    renderItem={(item) => (
                        <List.Item>
                            <List.Item.Meta
                                avatar={<SyncOutlined />}
                                title={item.action}
                                description={<Tag color="processing">{item.status}</Tag>}
                            />
                        </List.Item>
                    )}
                />
            </Card>

            <Title level={5} className="mb-4">
                Per-Integration Execution Control
            </Title>
            <List
                dataSource={integrations}
                renderItem={(item) => (
                    <List.Item>
                        <div className="flex items-center justify-between w-full">
                            <div className="flex items-center">
                                <Text strong>{item.name}</Text>
                                {item.notes && (
                                    <Text type="secondary" className="ml-2 text-xs">
                                        ({item.notes})
                                    </Text>
                                )}
                            </div>
                            <Switch checked={item.autoExecute} onChange={(checked) => handleIntegrationToggle(item.id, checked)} />
                        </div>
                    </List.Item>
                )}
            />
        </Card>
    )
}

export default ExecutionControl